import { useCallback, useEffect, useState } from 'react';
import ServicePaths from '../bridge/utils/ServicePaths';
import useRequest from './useRequest';
import LogRecord, { LogRecordType } from '../bridge/utils/LogRecord';
import { Bot } from '../screens/Detail/types';
import axios from 'axios';
import {
  WebsocketMessageType,
  WebsocketMessages,
} from '../bridge/utils/WebsocketMessages';
import useLogsWebsocket from './useLogsWebsocket';
import Services from '../bridge/utils/Services';

const getStartWebsocketServicePath = (botActivePort: number) => {
  return ServicePaths.BOT.StartLogsWebsocket(botActivePort);
};

const getWebsocketServicePath = (botActivePort: number) => {
  return ServicePaths.BOT.Websocket(botActivePort);
};

export const useErrorLogsWebsocket = () => {
  const { logRecords: dataLogRecords, isLoading: isDataLoading } =
    useLogsWebsocket(Services.Data, undefined, LogRecordType.Error);
  const {
    logRecords: candleFetcherLogRecords,
    isLoading: isCandleFetcherLoading,
  } = useLogsWebsocket(Services.CandleFetcher, undefined, LogRecordType.Error);
  const { logRecords: controlLogRecords, isLoading: isControlLoading } =
    useLogsWebsocket(Services.Control, undefined, LogRecordType.Error);
  const { logRecords: orderLogRecords, isLoading: isOrderLoading } =
    useLogsWebsocket(Services.Order, undefined, LogRecordType.Error);
  const { logRecords: exchangeLogRecords, isLoading: isExchangeLoading } =
    useLogsWebsocket(Services.Exchange, undefined, LogRecordType.Error);

  // This is a combination of all services and bots, but only the errors
  const [logRecords, setLogRecords] = useState<LogRecord[]>([]);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    if (!dataLogRecords?.length) {
      return;
    }

    setLogRecords((prev) => {
      const updated = [...prev, ...dataLogRecords];
      updated.sort((a, b) => a.timestamp - b.timestamp);
      return updated;
    });
  }, [dataLogRecords]);

  useEffect(() => {
    if (!candleFetcherLogRecords?.length) {
      return;
    }

    setLogRecords((prev) => {
      const updated = [...prev, ...candleFetcherLogRecords];
      updated.sort((a, b) => a.timestamp - b.timestamp);
      return updated;
    });
  }, [candleFetcherLogRecords]);

  useEffect(() => {
    if (!controlLogRecords?.length) {
      return;
    }

    setLogRecords((prev) => {
      const updated = [...prev, ...controlLogRecords];
      updated.sort((a, b) => a.timestamp - b.timestamp);
      return updated;
    });
  }, [controlLogRecords]);

  useEffect(() => {
    if (!orderLogRecords?.length) {
      return;
    }

    setLogRecords((prev) => {
      const updated = [...prev, ...orderLogRecords];
      updated.sort((a, b) => a.timestamp - b.timestamp);
      return updated;
    });
  }, [orderLogRecords]);

  useEffect(() => {
    if (!exchangeLogRecords?.length) {
      return;
    }

    setLogRecords((prev) => {
      const updated = [...prev, ...exchangeLogRecords];
      updated.sort((a, b) => a.timestamp - b.timestamp);
      return updated;
    });
  }, [exchangeLogRecords]);

  const { data: botsData } = useRequest(ServicePaths.DATA.GetAllActiveBots);

  const enableWebsocket = useCallback(async (port: number) => {
    try {
      const startWebsocketServicePath = getStartWebsocketServicePath(port);

      if (!startWebsocketServicePath) {
        return false;
      }

      const {
        data: { success },
      } = await axios({
        method: 'POST',
        url: startWebsocketServicePath,
      });

      return success;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!botsData?.bots.length) {
      return;
    }

    const states = (botsData.bots as Bot[]).map((b: Bot) => ({
      id: b.botId,
      isLoading: true,
    }));

    setLoadingStates((prev) => {
      const updated = { ...prev };

      states.forEach((state) => {
        updated[state.id] = state.isLoading;
      });

      return updated;
    });
  }, [botsData]);

  useEffect(() => {
    if (!botsData?.bots.length) {
      return;
    }

    botsData.bots.forEach(async (bot: Bot) => {
      if (!bot.activePort) {
        return;
      }

      const res = await enableWebsocket(bot.activePort);

      if (!res) {
        return;
      }

      const websocketServicePath = getWebsocketServicePath(bot.activePort);
      const ws = new WebSocket(websocketServicePath);

      ws.addEventListener('open', () => {
        setLoadingStates((prev) => {
          const updated = { ...prev };
          updated[bot.botId] = false;
          return updated;
        });
      });

      ws.addEventListener('message', (event) => {
        const message = JSON.parse(event.data) as WebsocketMessages;

        if (message.type === WebsocketMessageType.AllLogs) {
          const erroRecords = message.logRecords.filter(
            (r) => r.type === LogRecordType.Error,
          );
          setLogRecords((prev) => [...prev, ...erroRecords]);
        } else {
          if (message.logRecord.type === LogRecordType.Error) {
            setLogRecords((prev) => [...prev, message.logRecord]);
          }
        }
      });
    });
  }, [botsData, enableWebsocket]);

  useEffect(() => {
    if (!logRecords?.length) {
      return;
    }

    logRecords.sort((a, b) => a.timestamp - b.timestamp);
  }, [logRecords]);

  return {
    logRecords,
    isLoading: [
      isDataLoading,
      isCandleFetcherLoading,
      isControlLoading,
      isOrderLoading,
      isExchangeLoading,
      ...Object.values(loadingStates),
    ].some((isLoading) => isLoading),
  };
};
